// LB-17 §8.1: client twin of app/homes/listing_track.py. The track lives in
// home.asset_class; legacy post-close values (startup, oil_gas, debt,
// revenue_share, other) read as "company". Both mappings are driven by
// tests/fixtures/listing_track_cases.json.

export const TRACK_REAL_ESTATE = "real_estate";
export const TRACK_COMPANY = "company";
export const TRACK_FUND = "fund";

export const ISSUER_TRACKS = [TRACK_REAL_ESTATE, TRACK_COMPANY, TRACK_FUND];

export const REG_CF_NAMES = ["reg_cf", "debt_reg_cf"];

export function listingTrack(assetClass) {
  if (!assetClass || assetClass === TRACK_REAL_ESTATE) return TRACK_REAL_ESTATE;
  if (assetClass === TRACK_FUND) return TRACK_FUND;
  return TRACK_COMPANY;
}

export function isRealEstateListing(home) {
  return listingTrack(home && home.asset_class) === TRACK_REAL_ESTATE;
}

// The legal cell of the track x exemption matrix (server-enforced too).
export function fundRegCfConflict(assetClass, regulations) {
  return (
    listingTrack(assetClass) === TRACK_FUND &&
    (regulations || []).some((r) => REG_CF_NAMES.indexOf(r) !== -1)
  );
}

// Investor-page headline for a non-real-estate listing. Until LB-1 adds a
// headline column the headline is the custom-path street_name
// (additional_data.address.street_name). A listing that carries a real
// resolved address (formatted_street_address) has no custom headline, so
// its street is never shown; the company name is used instead.
export function storedHeadline(home) {
  const info = (home && home.info) || {};
  const address = info.address || {};
  if (address.formatted_street_address) return "";
  return (address.street_name || "").trim();
}

// The issuer dashboard card title (LB-26 R2-D #4): real estate -> its
// street; otherwise the headline; then the company name, then the server's
// display_title. Shared by the dashboard and the issuer closings pages
// (R6-14) so a listing never shows as its random ``home.name``.
export function listingDisplayTitle(home) {
  if (!home) return "";
  const info = home.info || {};
  const address = info.address || {};
  const company = home.company || {};
  const first = isRealEstateListing(home)
    ? (address.formatted_street_address || address.street_name || "").trim()
    : storedHeadline(home);
  return first || (company.name || "").trim() || home.display_title || "";
}

export function nonReHeadline(home) {
  const headline = storedHeadline(home);
  if (headline) return headline;
  const company = home && home.company;
  return ((company && company.name) || "").trim();
}

// Canonical wizard order (the store's current_lyp_step indexes into it).
export const WIZARD_STEP_ORDER = [
  "complete your bio",
  "property address",
  "build/edit listing",
  "determine equity",
  "propose terms",
  "investor incentives",
  "application review",
];

// Investor-page / builder tabs that only make sense for a property.
export const RE_ONLY_TABS = [
  "Details",
  "Sales History",
  "Historic Zipcode Appreciation",
];

export function isTabVisibleForTrack(tabName, home) {
  return isRealEstateListing(home) || RE_ONLY_TABS.indexOf(tabName) === -1;
}

// The builder step after Build Listing: Determine Equity is real-estate-only.
export function nextBuilderStep(home) {
  return isRealEstateListing(home) ? "determine-equity" : "propose-terms";
}

// Wizard steps a company or fund listing does not have. The HomeStep row
// stays on the server (no delete); it is skipped here and in the server's
// draft-progress count.
export const RE_ONLY_WIZARD_STEPS = ["determine equity"];

// LB-26 P1-7: wizard steps no track shows ("funding deadline": the close
// date lives in Propose Terms). Twin of the server's HIDDEN_WIZARD_STEPS
// (app/homes/listing_track.py); both are checked against
// tests/fixtures/listing_track_cases.json. Left out of draft-progress totals.
export const HIDDEN_WIZARD_STEPS = ["funding deadline"];

export function isWizardStepVisible(stepName, assetClass) {
  if (HIDDEN_WIZARD_STEPS.indexOf(stepName) !== -1) return false;
  return (
    listingTrack(assetClass) === TRACK_REAL_ESTATE ||
    RE_ONLY_WIZARD_STEPS.indexOf(stepName) === -1
  );
}

// i18n key for a wizard step label. For a company or fund listing the
// address step is the "Headline" step.
export function wizardStepLabelKey(stepName, fundingModel, assetClass) {
  if (
    listingTrack(assetClass) !== TRACK_REAL_ESTATE &&
    stepName === "property address"
  ) {
    return "listyourproperty.track.non_re.steps.headline";
  }
  return `listyourproperty.${fundingModel}.steps.${String(stepName).replaceAll(
    " ",
    "_"
  )}`;
}

// i18n key for the step a builder button names ("Next: Headline"), from
// the listing itself. Same key the step list shows (wizardStepLabelKey).
export function homeStepLabelKey(stepName, home, fundingModel) {
  const h = home || {};
  return wizardStepLabelKey(stepName, fundingModel || h.funding_model, h.asset_class);
}

// Copy that differs by what is being listed. ``choices`` is
// {real_estate, company, fund}; a missing kind falls back to the
// real-estate wording, and a missing home or asset_class reads as real
// estate (LB-17).
export function byTrack(home, choices) {
  const c = choices || {};
  const track = listingTrack(home && home.asset_class);
  return c[track] !== undefined ? c[track] : c[TRACK_REAL_ESTATE];
}

// R2-16: use-of-funds categories that describe a property. A company or
// fund listing is not offered them (nor pre-enabled with them).
export const RE_ONLY_ALLOCATION_CATEGORIES = [
  "Acquisition cost",
  "Closing costs",
  "Property management",
  "Marketing & leasing",
  "Capital improvements",
  "Renovation of this property",
];

export function isRealEstateOnlyAllocation(name) {
  return RE_ONLY_ALLOCATION_CATEGORIES.indexOf(String(name || "").trim()) !== -1;
}

// R2-16: Build Listing KPIs / metrics that only describe a property (by
// the label the menus show).
export const RE_ONLY_METRIC_LABELS = [
  "Cap Rate",
  "NOI",
  "GRM",
  "LTV",
  "DSCR",
  "Occupancy Rate",
  "Tenants",
  "Included Utilities",
];

export function isRealEstateOnlyMetric(metric) {
  const label = String((metric && metric.label) || "").trim();
  return RE_ONLY_METRIC_LABELS.indexOf(label) !== -1;
}
